import clsx from 'clsx'
import { orderBy } from 'lodash-es'
import type { PropsWithChildren } from 'react'

import { Icon } from '@fv/client-components'
import type { FullShipment, TrackDetail } from '@fv/client-types'
import { fvDateUtc } from '@fv/dates'
import type { TrackingStatus, Workflow } from '@fv/models'
import { getTrackEventDate } from '@fv/models/core'

import { ShipperTooltip } from '@/components/ShipperTooltip'

import type { SharedShipment } from '../share/types'
import { useTracking } from './queries'

const loadProgressMap: Record<
  TrackingStatus,
  { sort: number; label?: string }
> = {
  pending: { sort: 0, label: 'Scheduled' },
  'enroute-to-pickup': { sort: 1, label: 'Enroute to pickup' },
  'in-transit': { sort: 2, label: 'Picked up' },
  'out-for-delivery': { sort: 3, label: 'Out for delivery' },
  delivered: { sort: 4, label: 'Delivered' },
  'not-found': { sort: -1, label: 'Not found' },
  error: { sort: -1 },
}

const getException = (
  tracking: TrackDetail,
  status: TrackingStatus,
): string | undefined => {
  const eventsByStatus =
    tracking.trackEvents?.filter(e => e.status === status) ?? []
  const earliestEventByStatus = orderBy(
    eventsByStatus,
    h => getTrackEventDate(h.eventDateTime).toDate(),
    'asc',
  )?.[0]
  const earliestEventDate =
    earliestEventByStatus &&
    getTrackEventDate(earliestEventByStatus.eventDateTime)
  const eventsWithExceptionAfterDate =
    earliestEventDate &&
    tracking.trackEvents?.filter(
      event =>
        event.exceptionDescription &&
        getTrackEventDate(event.eventDateTime).isSameOrAfter(earliestEventDate),
    )
  return eventsWithExceptionAfterDate?.[0]?.exceptionDescription
}

const getTrackingSummary = (
  tracking: TrackDetail,
  exception?: string,
): string | undefined => {
  const lastUpdatedTimeStr = fvDateUtc(tracking.lastUpdatedDate).fromNow()
  const appendedException = exception ? ` - ${exception}` : ''

  if (tracking.currentStatus === 'pending') {
    return `Scheduled ~ Updated ${lastUpdatedTimeStr}${appendedException}`
  }
  if (tracking.currentStatus === 'in-transit') {
    if (tracking.currentLocation?.city && tracking.currentLocation?.state) {
      return `In ${tracking.currentLocation.city}, ${tracking.currentLocation.state} ~ ${lastUpdatedTimeStr}${appendedException}`
    } else {
      return `In Transit ~ ${lastUpdatedTimeStr}${appendedException}`
    }
  }
  if (tracking.currentStatus === 'out-for-delivery') {
    return `Out for delivery${appendedException}`
  }
  if (tracking.currentStatus === 'delivered') {
    const dateStr =
      tracking.deliveryDateActual &&
      fvDateUtc(tracking.deliveryDateActual).format('MMM D @ hh:mm A')
    return `Delivered ${dateStr ? `~ ${dateStr}` : ''}${appendedException}`
  }
  return undefined
}

const PointLabel = ({
  tooltip,
  currentStatus,
  children,
}: PropsWithChildren<{
  tooltip?: string
  currentStatus: TrackingStatus
}>) => {
  const span = (
    <span
      className={clsx(
        'slideRight absolute -top-[.325rem] left-10 whitespace-nowrap py-2 px-2 bg-fv-orange-50 z-30  border border-fv-orange before:w-3 before:h-3 before:rotate-45 before:border before:bg-fv-orange-50 before:border-l-0 before:border-b-0 before:absolute before:-right-[.4rem] before:top-1/2 before:-translate-y-1/2 before:border-fv-orange',
        currentStatus === 'in-transit' &&
          '!border-fv-beer-dark before:!border-fv-beer-dark !left-10 !bg-fv-beer-light before:!bg-fv-beer-light',
        currentStatus === 'out-for-delivery' &&
          '!border-fv-green before:!border-fv-green !left-10 !bg-fv-green-50 before:!bg-fv-green-50',
        currentStatus === 'delivered' &&
          '!border-fv-green !left-auto !right-12 before:!border-fv-green !bg-fv-green-50 before:!bg-fv-green-50',
        // Narrow screens can't fit the label beside the point, so drop it below the bar.
        'b600:!top-10 b600:before:hidden',
        currentStatus === 'pending' && 'b600:!left-0',
        (currentStatus === 'in-transit' ||
          currentStatus === 'out-for-delivery') &&
          'b600:!left-1/2 b600:!-translate-x-1/2',
        currentStatus === 'delivered' && 'b600:!right-0',
      )}
    >
      {children}
    </span>
  )
  return tooltip ? (
    <ShipperTooltip label={tooltip}>{span}</ShipperTooltip>
  ) : (
    span
  )
}

type PointProps = {
  status: TrackingStatus
  currentStatus: TrackingStatus
  track?: TrackDetail
  scheduled?: boolean
}
const BarPoint = ({ status, currentStatus, track, scheduled }: PointProps) => {
  const completed =
    loadProgressMap[currentStatus].sort > loadProgressMap[status].sort
  const active = currentStatus === status

  const exception = track && getException(track, status)
  const trackingSummary = track && getTrackingSummary(track, exception)

  let label = loadProgressMap[status].label
  if (status === 'pending' && scheduled) {
    label = 'Scheduled'
  }

  return (
    <li
      className={clsx(
        status,
        'relative w-8 h-8 bg-white border-fv-gray-300 border-[.2rem] rounded-full z-10 block after:absolute after:top-1/2 after:left-1/2 after:w-4 after:h-4 after:rounded-full after:block after:-translate-x-1/2 after:-translate-y-1/2 ',
        completed && 'after:bg-fv-gray-300 !border-fv-gray-500',
        active && 'border-fv-gray-500',
        active &&
          currentStatus === 'pending' &&
          'after:!bg-fv-orange-300 !border-fv-orange',
        active &&
          currentStatus === 'in-transit' &&
          '!border-fv-beer-dark after:bg-fv-beer-medium',
        active &&
          currentStatus === 'out-for-delivery' &&
          'after:bg-fv-green-300 !border-fv-green',
        active &&
          currentStatus === 'delivered' &&
          'after:bg-fv-green-300 !border-fv-green',
        {
          active,
          completed,
        },
      )}
    >
      {active && (
        <PointLabel tooltip={trackingSummary} currentStatus={currentStatus}>
          <span>{label}</span>
          {!!exception && (
            <Icon
              icon="exclamation-triangle"
              className="text-fv-orange-bright ml-1"
            />
          )}
        </PointLabel>
      )}
    </li>
  )
}

type BarProps = {
  schedulePickup: boolean
  tracking: TrackDetail
  workflow: Workflow
  currentStatus: TrackingStatus
  onClick?: () => void
}
const TrackingBar = ({
  onClick,
  workflow,
  currentStatus,
  schedulePickup,
  tracking,
}: BarProps) => {
  return (
    <ul
      className={clsx(
        'tracking-visual cursor-pointer relative my-6 b600:mb-16 flex items-center justify-between before:left-0 before:absolute before:w-full before:border-dotted before:border-t before:border-t-fv-gray-500 before:h-px after:h-[2px] after:absolute after:bg-fv-gray-500 transition-all after:w-0 after:left-0',
        currentStatus,
        currentStatus === 'in-transit' && 'after:w-[33%]',
        currentStatus === 'out-for-delivery' && 'after:w-[66%]',
        currentStatus === 'delivered' && 'after:w-[95%]',
      )}
      onClick={onClick}
    >
      <BarPoint
        status="pending"
        scheduled={schedulePickup}
        currentStatus={currentStatus}
        track={tracking}
      />
      {workflow === 'truckload' && (
        <BarPoint
          status="enroute-to-pickup"
          currentStatus={currentStatus}
          track={tracking}
        />
      )}
      <BarPoint
        status="in-transit"
        currentStatus={currentStatus}
        track={tracking}
      />
      {workflow !== 'truckload' && (
        <BarPoint
          status="out-for-delivery"
          currentStatus={currentStatus}
          track={tracking}
        />
      )}
      <BarPoint
        status="delivered"
        currentStatus={currentStatus}
        track={tracking}
      />
    </ul>
  )
}

type ShipmentBarProps = {
  load: FullShipment
  onClick?: () => void
}
export const ShipmentTrackingBar = ({ load, onClick }: ShipmentBarProps) => {
  const { data: tracking } = useTracking(load._id)
  return (
    <TrackingBar
      onClick={onClick}
      tracking={tracking}
      workflow={load.workflow}
      currentStatus={load.tracking?.currentStatus ?? 'pending'}
      schedulePickup={load.schedulePickup}
    />
  )
}

type SharedPageBarProps = {
  shipment: SharedShipment
}
export const SharedShipmentTrackingBar = ({ shipment }: SharedPageBarProps) => {
  return (
    <TrackingBar
      tracking={shipment.tracking}
      workflow={shipment.workflow}
      currentStatus={shipment.tracking?.currentStatus}
      schedulePickup={shipment.schedulePickup}
    />
  )
}
